import React, { memo, useState, useEffect, useMemo, useCallback } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'

import { Card } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, string, number, bool } from 'prop-types'

import ProductGridItem from 'Pods/Shared/ProductGridItem/ProductGridItem'

import useBreakpoint from 'Utils/useBreakpoint'

import HeaderSection from './Components/HeaderSection/HeaderSection'
import useCollectionItems from './hooks/useCollectionItems'
import { getViewAllUrl } from './Utils/getViewAllUrl'

const PREFETCH_THRESHOLD = 5

function CollectionModelsArea({
  products: initialProducts,
  numberOfModels,
  slug,
  next: hasMoreInitially = false,
}) {
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs'

  const [isHydrated, setIsHydrated] = useState(false)
  const [swiperInstance, setSwiperInstance] = useState(null)

  const { products, fetchNextPage, hasNextPage, isFetchingNextPage } = useCollectionItems(
    initialProducts,
    slug,
    hasMoreInitially
  )

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  useEffect(() => {
    if (!isMobile || !swiperInstance) {
      return
    }

    const swiperEl = swiperInstance.el

    if (!swiperEl) {
      return
    }

    const handleTouchStart = (e) => {
      swiperEl.dataset.touchStartY = e.touches[0].pageY
    }

    const handleTouchMove = (e) => {
      const touchStartY = parseFloat(swiperEl.dataset.touchStartY || 0)
      const touchCurrentY = e.touches[0].pageY
      const touchDiffY = Math.abs(touchCurrentY - touchStartY)

      if (touchDiffY < 10) {
        e.preventDefault()
      }
    }

    swiperEl.addEventListener('touchstart', handleTouchStart, { passive: true })
    swiperEl.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      swiperEl.removeEventListener('touchstart', handleTouchStart)
      swiperEl.removeEventListener('touchmove', handleTouchMove)
    }
  }, [isMobile, swiperInstance])

  const href = useMemo(() => getViewAllUrl(slug), [slug])

  const handleSlideChange = useCallback(
    (swiper) => {
      if (!isMobile) {
        return
      }

      const currentIndex = swiper.activeIndex
      const totalItems = products.length
      const itemsRemaining = totalItems - currentIndex - 1

      if (itemsRemaining <= PREFETCH_THRESHOLD && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [isMobile, products.length, hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  const renderProducts = () => {
    if (!isHydrated || !isMobile || products.length === 1) {
      return (
        <div className="collection-models-area__products">
          {products.map((item) => (
            <ProductGridItem key={item.id} {...item} />
          ))}
        </div>
      )
    }

    return (
      <Swiper
        onSwiper={setSwiperInstance}
        onSlideChange={handleSlideChange}
        className="collection-models-area__swiper"
        slidesPerView="auto"
        spaceBetween={8}
        grabCursor
        freeMode
      >
        {products.map((item) => (
          <SwiperSlide key={item.id} className="collection-models-area__swiper-slide">
            <ProductGridItem {...item} />
          </SwiperSlide>
        ))}
      </Swiper>
    )
  }

  return (
    <Card hasBorder isRounded customClass="collection-models-area">
      <HeaderSection
        href={href}
        numberOfModels={numberOfModels}
      />
      {renderProducts()}
    </Card>
  )
}

CollectionModelsArea.propTypes = {
  products: array.isRequired,
  numberOfModels: number.isRequired,
  slug: string.isRequired,
  next: bool,
}

export default memo(CollectionModelsArea)
